import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Users, BedDouble, Bath, Waves, Wifi, Tv, CheckCircle2, ArrowRight, CalendarDays, ExternalLink, Loader2 } from "lucide-react";
import { CONTACT } from "../mock";
import { useMedia, resolveUrl } from "../context/MediaContext";
import { useToast } from "../hooks/use-toast";
import PageHero from "../components/PageHero";
import { Reveal, Magnetic, EASE } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITIES = [
  { icon: Waves, label: "Private Pool & Spa" },
  { icon: Wifi, label: "High-speed WiFi" },
  { icon: Tv, label: "Netflix & Smart TV" },
  { icon: BedDouble, label: "3 Bedrooms" },
  { icon: Bath, label: "2 Bathrooms" },
  { icon: Users, label: "Sleeps up to 8" },
];

// Airbnb listing link (editable).
const AIRBNB_URL = "https://www.airbnb.com/rooms/54293008";

function toDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function fmt(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function BookingPage() {
  const { toast } = useToast();
  const { slots } = useMedia();
  const [config, setConfig] = useState({ nightly_rate: 325, cleaning_fee: 120, min_nights: 2, airbnb_connected: false });
  const [blocked, setBlocked] = useState([]);
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "2", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/booking-config`),
        axios.get(`${API}/availability?refresh=1&_=${Date.now()}`),
      ]);
      setConfig(c.data);
      setBlocked(a.data.blocked || []);
    } catch (e) {
      console.error("Failed to load availability", e);
    }
  };
  useEffect(() => { load(); }, []);

  const disabledDays = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return [{ before: today }, ...blocked.map(toDate)];
  }, [blocked]);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.round((range.to - range.from) / 86400000);
  }, [range]);

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) { toast({ title: "Pick your dates", description: "Select check-in and check-out on the calendar." }); return; }
    if (nights < config.min_nights) { toast({ title: "Minimum stay", description: `This home has a ${config.min_nights}-night minimum.` }); return; }
    if (!form.name || !form.email) { toast({ title: "Add your details", description: "Name and email are required." }); return; }
    if (!form.phone || form.phone.replace(/[^0-9]/g, "").length < 7) { toast({ title: "Phone number required", description: "Please enter a valid phone number so we can reach you." }); return; }
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: form.name, email: form.email, phone: form.phone,
        checkin: iso(range.from), checkout: iso(range.to),
        guests: Number(form.guests), message: form.message,
      });
      setDone(data);
      toast({ title: "Request sent!", description: "We'll confirm your dates by email shortly." });
      setRange(undefined);
      setForm({ name: "", email: "", phone: "", guests: "2", message: "" });
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Something went wrong. Please try again.";
      toast({ title: "Couldn't complete request", description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white/70 border border-[#0A3B2E]/10 rounded-xl px-4 py-3 text-[#16241E] placeholder:text-[#16241E]/40 focus:outline-none focus:ring-2 focus:ring-[var(--moss)]/40 focus:border-[var(--moss)] transition";

  return (
    <div className="bg-[#F4ECE0] min-h-screen">
      <PageHero eyebrow="Reserve Your Stay" title="Book Your" accent="Escape" image={resolveUrl(slots.cardRGV)}
        subtitle="Check live availability and request your dates directly — or book on Airbnb." />

      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
        {/* Left: details */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <Reveal>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#0A3B2E]/5 px-4 py-1.5 font-body font-semibold text-sm text-[#0A3B2E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--moss)]" /> {config.min_nights}-night minimum
              </span>
            </div>
            <p className="text-[#16241E]/65 mt-4 leading-relaxed font-light">
              A cozy 3BD/2BA haven with a private pool, spa &amp; tanning ledge. Fully furnished with WiFi,
              Netflix &amp; spotless surroundings.
            </p>
            <p className="text-[#16241E]/65 mt-3 leading-relaxed font-light">
              Send us your dates below and we'll reach out with availability &amp; pricing for
              overnight stays or events.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-3 mt-8">
              {AMENITIES.map((a) => (
                <div key={a.label} className="flex items-center gap-3 bg-white/60 border border-white/70 rounded-xl px-4 py-3">
                  <a.icon size={19} className="text-[var(--moss-deep)] shrink-0" />
                  <span className="text-sm text-[#16241E] font-medium">{a.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 rounded-[22px] overflow-hidden shadow-xl h-56">
              <img src={resolveUrl(slots.poolSpa)} alt="Pool" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-7 space-y-2.5">
              {[`Check-in ${CONTACT.checkIn}, Check-out ${CONTACT.checkOut}`, "Free private parking", "Late check-out available, ask for rate"].map((t) => (
                <p key={t} className="flex items-center gap-2 text-[#16241E]/75 text-sm"><CheckCircle2 size={17} className="text-[var(--moss)] shrink-0" /> {t}</p>
              ))}
            </div>
            <a href={AIRBNB_URL} target="_blank" rel="noreferrer" data-cursor="hover"
              className="inline-flex items-center gap-2 mt-6 text-[#0A3B2E] font-body font-semibold hover:text-[var(--moss-deep)] transition-colors">
              Prefer Airbnb? View the listing <ExternalLink size={16} />
            </a>
          </Reveal>
        </div>

        {/* Right: calendar + form */}
        <div className="lg:col-span-3 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="rounded-[26px] bg-white/70 backdrop-blur-sm border border-white/70 shadow-[0_25px_60px_rgba(10,59,46,0.12)] p-6 md:p-8"
          >
            {done ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full sunset-fill grid place-items-center mx-auto mb-5"><CheckCircle2 size={32} className="text-white" /></div>
                <h3 className="font-display font-bold text-2xl text-[#0A3B2E]">Request received!</h3>
                <div className="text-[#16241E]/75 mt-4 max-w-md mx-auto space-y-3 text-left sm:text-center text-[15px]">
                  <p>Hello, and thank you for reaching out to The Jackson House! We've received your inquiry and will get back to you as quickly as possible.</p>
                  <p>For the fastest assistance, you're also welcome to text us at <a href="tel:+19562425130" className="font-semibold text-[var(--moss-deep)] underline">956-242-5130</a>.</p>
                  <p>We'd be honored to host your special event at The Jackson House — from birthdays and parties to pool days, girls' nights, showers, and more.</p>
                  <p>We look forward to connecting with you soon!<br /><span className="font-semibold">The Jackson House Team</span></p>
                </div>
                <button onClick={() => setDone(null)} className="mt-7 sunset-fill text-white font-body font-bold px-7 py-3 rounded-full">Make another request</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <CalendarDays size={20} className="text-[var(--moss-deep)]" />
                  <h3 className="font-display font-bold text-xl text-[#0A3B2E]">Select your dates</h3>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#16241E]/60 mb-4">
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full sunset-fill inline-block" /> Selected</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#16241E]/15 inline-block" /> Booked / unavailable</span>
                  {config.airbnb_connected && <span className="text-[var(--moss-deep)] font-semibold">Synced with Airbnb</span>}
                </div>

                <div className="tjh-cal rounded-2xl bg-white/60 border border-[#0A3B2E]/10 p-2 flex justify-center">
                  <DayPicker
                    mode="range"
                    numberOfMonths={1}
                    selected={range}
                    onSelect={setRange}
                    disabled={disabledDays}
                    excludeDisabled
                    fromDate={new Date()}
                  />
                </div>

                {range?.from && range?.to && (
                  <div className="bg-[#0A3B2E]/5 rounded-xl p-4 mt-5 space-y-2 text-sm">
                    <div className="flex justify-between text-[#16241E]/80 font-medium"><span>{fmt(range.from)} &rarr; {fmt(range.to)}</span><span>{nights} {nights === 1 ? "night" : "nights"}</span></div>
                    <div className="flex justify-between text-[#16241E]/60 pt-2 border-t border-[#0A3B2E]/10"><span>Pricing</span><span>Sent after we confirm your dates</span></div>
                  </div>
                )}

                <form onSubmit={submit} className="mt-5 space-y-3.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" required placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="booking-name" />
                    <input type="email" required placeholder="Email address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="booking-email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="tel" required placeholder="Phone number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="booking-phone" />
                    <select value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className={`${inputCls} bg-white`}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>)}
                    </select>
                  </div>
                  <textarea rows={3} placeholder="Anything we should know? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
                  <Magnetic strength={0.25}>
                    <button type="submit" disabled={submitting} className="btn-shine w-full flex items-center justify-center gap-2 sunset-fill text-white font-body font-bold py-4 rounded-full disabled:opacity-70">
                      {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Request Availability &amp; Pricing <ArrowRight size={18} /></>}
                    </button>
                  </Magnetic>
                  <p className="text-xs text-center text-[#16241E]/50">No payment now &mdash; we'll confirm availability &amp; pricing by email shortly.</p>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
